import React, { useState, useEffect, useCallback } from "react";
import Layout from "../Components/Layout";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  useMediaQuery,
  useTheme,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  AppBar,
  Toolbar,
  Slide,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import ForumIcon from "@mui/icons-material/Forum";
import ScienceIcon from "@mui/icons-material/Science";
import VoiceChatIcon from "@mui/icons-material/VoiceChat";
import WidgetsIcon from "@mui/icons-material/Widgets";
import IntegrationIcon from "@mui/icons-material/Extension";
import Widget from "../pages/Agency/Widget";
import Voices from "./Voices";
import TestZone from "./TestZone";
// import Integration from "./Integration";
import Conversations from "./Conversations";
import { forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useCallOnce from "../hooks/useCallOnce";
// import { getUsersListForDropdown } from "../redux/actions/userActions";
import { getAgencyReferredUsers } from "../redux/actions/agencyActions";

const FullScreenTransition = forwardRef(function FullScreenTransition(
  props,
  ref
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function Agency() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const { list, loading, pagination } = useSelector(
    (state) => state.agency
  );
  // const { dropdown } = useSelector((state) => state.users);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    search: "",
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailModalOpen, setUserDetailModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useCallOnce(
    "agency/initial-load",
    () => {
      if (!loading) {
        const apiPage = page + 1;
        dispatch(getAgencyReferredUsers({ page: apiPage, limit: rowsPerPage }));
      }
    },
    [dispatch, loading]
  );

  // Removed brand dropdown for Agency page

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const apiPage = newPage + 1;
    const payload = {
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      search: filters.search || undefined,
      page: apiPage,
      limit: rowsPerPage,
    };
    dispatch(getAgencyReferredUsers(payload));
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    const payload = {
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      search: filters.search || undefined,
      page: 1,
      limit: newRowsPerPage,
    };
    dispatch(getAgencyReferredUsers(payload));
  };

  const extractRow = (item) => {
    return {
      name: item.name || "-",
      email: item.email || "-",
      createdAt: item.createdAt || item.created_at || "-",
      phoneNumber: item.phoneNumber || item.phone || "-",
      website: item.website || item.storeUrl || item.store_url || "-",
      referralCount: item.referralCount ?? item.referrals ?? 0,
      id: item._id || item.id || item.userId || "-",
    };
  };

  const pagedRows = list;

  const formatDateForUi = (isoDateString) => {
    if (!isoDateString) return "";
    const [year, month, day] = isoDateString.split("-");
    if (!year || !month || !day) return "";
    return `${month}/${day}/${year}`;
  };

  const UiDateField = ({ label, value, onChange, min, max }) => {
    const hiddenDateRef = React.useRef(null);

    const openPicker = () => {
      const el = hiddenDateRef.current;
      if (!el) return;
      try {
        el.focus();
      } catch (_) {}
      if (typeof el.showPicker === "function") {
        try {
          el.showPicker();
        } catch (_) {}
      } else {
        try {
          el.click();
        } catch (_) {}
      }
    };

    return (
      <Box
        sx={{ position: "relative" }}
        onMouseDown={(e) => {
          e.preventDefault();
          openPicker();
        }}
      >
        <TextField
          fullWidth
          label={label}
          type="text"
          placeholder={"MM/DD/YYYY"}
          value={formatDateForUi(value || "")}
          onFocus={openPicker}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.875rem" } }}
          inputProps={{ readOnly: true }}
        />

        <input
          ref={hiddenDateRef}
          type="date"
          value={value || ""}
          min={min}
          max={max}
          onChange={(e) => onChange(e.target.value || "")}
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0,
            width: "100%",
            height: "100%",
            cursor: "pointer",
            zIndex: 10,
            pointerEvents: "auto",
          }}
        />
      </Box>
    );
  };

  const renderDate = (value) => {
    if (!value) return "-";
    const date =
      typeof value === "number" ? new Date(value * 1000) : new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "America/New_York",
    }).format(date);
  };

  const handleOpenUserDetail = (item) => {
    setSelectedUser(item);
    const agencyId = item?._id || item?.id || item?.userId;
    const agencyName = item?.name || item?.company || item?.user?.company || extractRow(item).name;
    navigate("/agencydetails", { state: { agencyId, agencyName } });
  };

  const handleCloseUserDetail = () => {
    setUserDetailModalOpen(false);
  };

  return (
    <Layout>
      <Box sx={{ mt: 0, px: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "start",
            flexDirection: "column",
            alignItems: "start",
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Agency
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            Manage and view agency
          </Typography>
        </Box>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <UiDateField
                label="Start Date"
                value={filters.startDate}
                onChange={(val) =>
                  setFilters((f) => ({ ...f, startDate: val }))
                }
                max={filters.endDate || undefined}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <UiDateField
                label="End Date"
                value={filters.endDate}
                onChange={(val) => setFilters((f) => ({ ...f, endDate: val }))}
                min={filters.startDate || undefined}
              />
            </Grid>

            {/* Removed Select Brand filter */}

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Name, Email, Website, Phone"
                value={filters.search}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, search: e.target.value }))
                }
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.875rem" } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexDirection: isMobile ? "column" : "row",
                  "& > *": {
                    flex: isMobile ? 1 : "none",
                  },
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => {
                    setPage(0);
                    dispatch(getAgencyReferredUsers({
                      startDate: filters.startDate || undefined,
                      endDate: filters.endDate || undefined,
                      search: filters.search || undefined,
                      page: 1,
                      limit: rowsPerPage,
                    }));
                  }}
                  size="small"
                  sx={{
                    bgcolor: "#111827",
                    "&:hover": { bgcolor: "#0b1220" },
                    textTransform: "none",
                  }}
                >
                  Filter
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const reset = { startDate: "", endDate: "", search: "" };
                    setFilters(reset);
                    // setUserInput("");
                    setPage(0);
                    dispatch(getAgencyReferredUsers({ page: 1, limit: rowsPerPage }));
                  }}
                  size="small"
                  sx={{
                    borderColor: "#111827",
                    color: "#111827",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#0b1220",
                      backgroundColor: "#f9fafb",
                    },
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper}>
          <Table
            stickyHeader
            sx={{
              tableLayout: 'fixed',
              '& td, & th': {
                px: { xs: 1, sm: 2 },
                py: { xs: 0.75, sm: 1 },
              },
              '& td .MuiTypography-root': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc", py: 1, minWidth: 70, maxWidth: 70, width: 70}}>S. No</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc", py: 1, minWidth: 160, maxWidth: 160, width: 160 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc", py: 1, minWidth: 200, maxWidth: 200, width: 200 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc", py: 1, minWidth: 160, maxWidth: 160, width: 160 }}>Created At</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc", py: 1, minWidth: 160, maxWidth: 160, width: 160 }}>Phone Number</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc", py: 1, minWidth: 200, maxWidth: 200, width: 200 }}>Website</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc", py: 1, minWidth: 140, maxWidth: 140, width: 140 }}>Referral Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{ textAlign: "center", py: 8, height: 300 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body1" sx={{ color: "#6b7280" }}>
                        Loading users...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : list.length > 0 ? (
                pagedRows.map((item, index) => {
                  const row = extractRow(item);
                  return (
                    <TableRow
                      key={row.id || index}
                      hover
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#f8fafc" },
                      }}
                      onClick={() => handleOpenUserDetail(item)}
                    >
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          {page * rowsPerPage + index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ color: "#374151", fontWeight: 500 }}>
                          {row.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>{row.email}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>{renderDate(row.createdAt)}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>{row.phoneNumber}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: "#6b7280",
                            cursor: row.website && row.website !== "-" ? "pointer" : "default",
                            textDecoration: row.website && row.website !== "-" ? "underline" : "none",
                            "&:hover": {
                              color: row.website && row.website !== "-" ? "#111827" : "#6b7280"
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (row.website && row.website !== "-") {
                              const url = row.website.startsWith('http') ? row.website : `https://${row.website}`;
                              window.open(url, '_blank', 'noopener,noreferrer');
                            }
                          }}
                        >
                          {row.website}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>{row.referralCount}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={pagination.totalCount || list.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid #e5e7eb",
            "& .MuiTablePagination-toolbar": {
              paddingLeft: 2,
              paddingRight: 2,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "0.875rem",
                color: "#6b7280",
              },
            "& .MuiTablePagination-select": {
              fontSize: "0.875rem",
            },
          }}
        />
      </Box>

      {/* Dialog removed for navigation to dedicated page */}
      {/* <Dialog
        open={userDetailModalOpen}
        onClose={handleCloseUserDetail}
        fullScreen
        TransitionComponent={FullScreenTransition}
        TransitionProps={{ onExited: () => setSelectedUser(null) }}
      >
        <AppBar
          position="sticky"
          color="default"
          elevation={0}
          sx={{ borderBottom: "1px solid #e5e7eb" }}
        >
          <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={handleCloseUserDetail}
                edge="start"
                size="small"
                sx={{ mr: 1 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                
                {selectedUser
                  ? extractRow(selectedUser).name
                  : "Agency Details"}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseUserDetail} edge="end" size="small">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <DialogContent sx={{ p: 0, display: "flex", height: "100%" }}>
          <Box
            sx={{
              width: 250,
              borderRight: "1px solid #e5e7eb",
              backgroundColor: "#f9fafb",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb" }}>
              <Typography
                variant="subtitle2"
                sx={{ color: "#6b7280", fontWeight: 600 }}
              >
                Team Details
              </Typography>
            </Box>

            <Box sx={{ flex: 1, py: 1 }}>
              {[
                { label: "Widget", icon: <WidgetsIcon fontSize="small" /> },
                // {
                //   label: "Conversations",
                //   icon: <ForumIcon fontSize="small" />,
                // },
                // { label: "Voices", icon: <VoiceChatIcon fontSize="small" /> },
                // { label: "Test Zone", icon: <ScienceIcon fontSize="small" /> },
                // {
                //   label: "Integration",
                //   icon: <IntegrationIcon fontSize="small" />,
                // },
              ].map((item, index) => (
                <Box
                  key={index}
                  onClick={() => setActiveTab(index)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    backgroundColor:
                      activeTab === index ? "#111827" : "transparent",
                    color: activeTab === index ? "white" : "#374151",
                    "&:hover": {
                      backgroundColor:
                        activeTab === index ? "#111827" : "#f3f4f6",
                    },
                    transition: "all 0.2s ease",
                    borderLeft:
                      activeTab === index
                        ? "3px solid #111827"
                        : "3px solid transparent",
                  }}
                >
                  <Typography
                    sx={{
                      mr: 1.5,
                      fontSize: "16px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {item.icon}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: activeTab === index ? 600 : 400 }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto" }}>
            <style>{`
              .embedded-page header,
              .embedded-page nav[aria-label="sidebar"],
              .embedded-page .MuiDrawer-root,
              .embedded-page .MuiDrawer-paper {
                display: none !important;
              }
            `}</style>
            <Box className="embedded-page" sx={{ p: 0, height: "100%" }}>
              {activeTab === 0 && (
                <Widget
                  selectedUserId={
                    (selectedUser?._id || selectedUser?.userId || selectedUser?.id) || undefined
                  }
                />
              )}
              {activeTab === 1 && (
                <Conversations
                  selectedUserId={
                    (selectedUser?._id || selectedUser?.userId || selectedUser?.id) || undefined
                  }
                />
              )}
              {activeTab === 2 && <Voices />}
              {activeTab === 3 && <TestZone />}
              {activeTab === 4 && <Integration />}
            </Box>
          </Box>
        </DialogContent>
      </Dialog> */}
    </Layout>
  );
}

export default Agency;


